import { useAppSelector } from 'app/hooks';
import { useEffect, useMemo, useState } from 'react';
import {
  computeLayoutImageMetrics,
  LayoutImageMetrics
} from 'utils/image-scale';
import { resolveLevelLayoutAttachmentId } from 'utils/level-layout-image';

export type { LayoutImageMetrics };

export type LayoutImageInfo = {
  url: string;
  startPositionX: number;
  startPositionY: number;
  scale: number;
};

export default function useLayoutImageMetrics(): {
  metrics: LayoutImageMetrics | null;
  imageInfo: LayoutImageInfo | null;
} {
  const { structureAttachments, structureImageAssignments, structure } =
    useAppSelector((state) => state.structure);
  const { activeLevelIndex } = useAppSelector((state) => state.ui);

  const imageInfo = useMemo((): LayoutImageInfo | null => {
    if (structureAttachments.length === 0) return null;

    const attachmentId = resolveLevelLayoutAttachmentId({
      structure,
      activeLevelIndex,
      structureImageAssignments
    });

    if (attachmentId === null) return null;

    const attachment = structureAttachments.find((a) => a.id === attachmentId);
    const assignment = structureImageAssignments[attachmentId];

    if (!attachment?.url) return null;

    return {
      url: attachment.url,
      startPositionX: assignment?.startPositionX ?? 0,
      startPositionY: assignment?.startPositionY ?? 0,
      scale: assignment?.scale ?? 1
    };
  }, [
    structureAttachments,
    structureImageAssignments,
    structure,
    activeLevelIndex
  ]);

  const [imageDims, setImageDims] = useState<{
    w: number;
    h: number;
  } | null>(null);

  useEffect(() => {
    if (!imageInfo?.url) {
      setImageDims(null);
      return undefined;
    }

    let cancelled = false;
    const img = new Image();
    const applyDims = () => {
      if (cancelled) return;
      if (img.naturalWidth <= 0 || img.naturalHeight <= 0) return;
      setImageDims({ w: img.naturalWidth, h: img.naturalHeight });
    };

    img.onload = applyDims;
    img.src = imageInfo.url;
    if (img.complete) {
      applyDims();
    }

    return () => {
      cancelled = true;
    };
  }, [imageInfo?.url]);

  const metrics = useMemo(() => {
    if (!imageInfo || !imageDims) return null;
    return computeLayoutImageMetrics(
      imageDims.w,
      imageDims.h,
      imageInfo.startPositionX,
      imageInfo.startPositionY,
      imageInfo.scale
    );
  }, [imageInfo, imageDims]);

  return { metrics, imageInfo };
}
